import clsx from 'clsx';
import { memo } from 'react';

import type { SharedIconProps } from '../../types/SharedIconProps';

import { useNumberFormat } from '../../context/NumberFormat/NumberFormat';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { resolveCountBadgeCountText } from './utils';
import './CountBadge.css';

/** EDS-internal only — never set from src/**, only passed sibling-to-sibling within packages/eds. */
type CountBadgePrivateProps = {
  /** Seats the badge against a host's trailing edge: square corners, symmetric
   * padding, and the host's full height. Set by hosts that render the badge as
   * their last element (e.g. `Chip`). */
  mode?: 'attached';
};

export type CountBadgeProps = {
  /** Raw numeric count used for render gating and overflow threshold logic. */
  count: number;
  /** Optional leading icon. */
  icon?: SharedIconProps;
  /** Maximum count before the value is treated as overflow. Uncapped by
   * default — the full count renders. */
  maxCount?: number;
  __private?: CountBadgePrivateProps;
};

export const CountBadge = memo(
  ({ count, icon, maxCount, __private }: CountBadgeProps) => {
    const formatNumber = useNumberFormat();

    if (count <= 0) {
      return null;
    }

    const isOverflow = maxCount !== undefined && count > maxCount;
    const countText = resolveCountBadgeCountText(count, formatNumber, maxCount);

    return (
      <div
        className={clsx(
          'eds-count-badge',
          __private?.mode === 'attached' && 'eds-count-badge--attached',
          icon && 'eds-count-badge--has-icon',
          isOverflow && 'eds-count-badge--overflow',
        )}
      >
        {icon && (
          <Icon
            filled={icon.filled}
            icon={icon.key}
            label={icon.label}
            size="xs"
          />
        )}
        <Text truncate={1} variant="body-semi-s">
          {countText}
        </Text>
      </div>
    );
  },
);

CountBadge.displayName = 'CountBadge';
